import {
  getMasterVariantIds,
  getVariantChannelEntry,
  isMasterVariantLiveOnChannel,
  isVariantChannelDraft,
} from '../../../../store/products/helpers/listingSyncHelpers';
import {
  filterGalleryAssets,
  filterVariantAssets,
  getVariantAssetIdsFromAssets,
  PRODUCT_VIEW_LAYER,
} from '../../../AssetTargetsDialog/helpers/assetProductLinks';
import {
  hasDraftListingForChannel,
  hasLiveListingForChannel,
} from './syncFlowHelpers';

/** Matches CHANNEL_SYNC_ACTION.PUBLISH without importing syncFlowOrchestrator (circular). */
const CHANNEL_ACTION_PUBLISH = 'publish';

const getListingMediaLayerForChannelAction = (channelAction) => (
  channelAction === CHANNEL_ACTION_PUBLISH
    ? PRODUCT_VIEW_LAYER.LIVE
    : PRODUCT_VIEW_LAYER.STAGING
);

/**
 * Prefill must match setup warning gates (ignore orphan asset links / legacy channels):
 * - create draft → listing DRAFT (product) or listingVariant DRAFT (variant sync)
 * - publish to live → listing/variant live present
 */
const shouldPrefillListingMediaForChannelAction = ({
  channelAction,
  productListings,
  channelId,
  product,
  productsState,
  masterVariantIds,
}) => {
  if (channelAction === CHANNEL_ACTION_PUBLISH) {
    return hasLiveListingForChannel({
      channelId,
      product,
      productsState,
      productListings,
      masterVariantIds,
    });
  }

  return hasDraftListingForChannel({
    channelId,
    product,
    productsState,
    productListings,
    masterVariantIds,
  });
};

export const createEmptyProductMediaByChannel = (channelIds) => (
  channelIds.reduce((acc, channelId) => ({ ...acc, [channelId]: [] }), {})
);

export const createEmptyVariantMediaByChannel = (channelIds) => (
  channelIds.reduce((acc, channelId) => ({ ...acc, [channelId]: {} }), {})
);

export const getProductMediaAssets = (product, productsState, productId = product?._id) => {
  const entityAssets = productId && productsState?.entities?.assets?.masterAssets?.[productId];
  if (entityAssets?.length) {
    return entityAssets;
  }

  return product?.assets || [];
};

export const getMasterVariantAssets = (product, variantId, productsState) => {
  const entityAssets = productsState?.entities?.assets?.masterVariantAssets?.[variantId];
  if (entityAssets?.length) {
    return entityAssets;
  }

  return product?.variantAssetsById?.[variantId] || [];
};

export const getAllMasterVariantAssets = (product, productsState) => {
  const variantIds = product?.variants?.map(({ _id }) => _id) || [];

  if (variantIds.length) {
    return variantIds.flatMap((variantId) => getMasterVariantAssets(product, variantId, productsState));
  }

  return Object.values(product?.variantAssetsById || {}).flat();
};

const getAllAssetsForVariantLookup = (product, productsState) => (
  [
    ...getProductMediaAssets(product, productsState),
    ...getAllMasterVariantAssets(product, productsState),
  ]
);

export const getAttachedAssetsForVariant = (product, variantId, productsState) => {
  const allAssets = getAllAssetsForVariantLookup(product, productsState);
  const seenAssetIds = new Set();
  const attachedAssets = [];

  getVariantAssetIdsFromAssets(allAssets, variantId).forEach((assetId) => {
    if (seenAssetIds.has(assetId)) return;
    const asset = allAssets.find(({ _id }) => String(_id) === String(assetId));
    if (!asset) return;
    seenAssetIds.add(assetId);
    attachedAssets.push(asset);
  });

  getMasterVariantAssets(product, variantId, productsState).forEach((asset) => {
    if (seenAssetIds.has(String(asset._id))) return;
    seenAssetIds.add(String(asset._id));
    attachedAssets.push(asset);
  });

  return attachedAssets;
};

export const getVariantMediaAssets = (product, masterVariantIds, productsState) => {
  const variantIds = getMasterVariantIds(product, masterVariantIds);
  const seenAssetIds = new Set();
  const variantAssets = [];

  variantIds.forEach((variantId) => {
    getAttachedAssetsForVariant(product, variantId, productsState).forEach((asset) => {
      const assetId = String(asset._id);
      if (seenAssetIds.has(assetId)) return;
      seenAssetIds.add(assetId);
      variantAssets.push(asset);
    });
  });

  return variantAssets;
};

export const getSelectedVariantIdsForSync = (product, masterVariantIds) => (
  getMasterVariantIds(product, masterVariantIds)
);

/**
 * Keeps prior media selections only for channels whose draft/publish action did not change.
 * Channels with a new action are omitted so buildInitial* can re-prefill for that layer.
 */
export const retainMediaByChannelForUnchangedActions = ({
  mediaByChannel = {},
  previousActionsByChannelId = {},
  nextActionsByChannelId = {},
  selectedChannels = [],
}) => selectedChannels.reduce((acc, channelId) => {
  if (
    Object.prototype.hasOwnProperty.call(mediaByChannel, channelId)
    && previousActionsByChannelId[channelId] === nextActionsByChannelId[channelId]
  ) {
    acc[channelId] = mediaByChannel[channelId];
  }
  return acc;
}, {});

export const buildInitialProductMediaByChannel = ({
  selectedChannels = [],
  product,
  productsState,
  productListings = {},
  channelActionsByChannelId = {},
  existingProductMediaByChannel = {},
}) => {
  const masterAssetIds = getProductMediaAssets(product, productsState, product?._id)
    .map(({ _id }) => String(_id));

  return selectedChannels.reduce((acc, channelId) => {
    if (Object.prototype.hasOwnProperty.call(existingProductMediaByChannel, channelId)) {
      acc[channelId] = existingProductMediaByChannel[channelId];
      return acc;
    }

    const listingProductId = productListings?.[channelId]?.listingProduct?._id;
    if (!listingProductId) {
      acc[channelId] = [];
      return acc;
    }

    const channelAction = channelActionsByChannelId[channelId];
    if (!shouldPrefillListingMediaForChannelAction({
      channelAction,
      productListings,
      channelId,
      product,
      productsState,
    })) {
      acc[channelId] = [];
      return acc;
    }

    const listingAssets = productsState?.entities?.assets?.listingAssets?.[listingProductId] || [];
    const layer = getListingMediaLayerForChannelAction(channelAction);
    const layerAssetIds = new Set(
      filterGalleryAssets(listingAssets, { productId: listingProductId, layer })
        .map(({ _id }) => String(_id)),
    );

    acc[channelId] = masterAssetIds.filter((assetId) => layerAssetIds.has(assetId));
    return acc;
  }, {});
};

const buildChannelVariantMediaAssignments = ({
  channelId,
  product,
  productsState,
  productListings,
  masterVariantIds,
  mediaLayer,
}) => {
  const assignments = {};
  const assignedVariantIds = new Set();

  masterVariantIds.forEach((masterVariantId) => {
    const listingVariant = getVariantChannelEntry(productsState, masterVariantId, channelId)
      || getVariantChannelEntry(product, masterVariantId, channelId);
    const listingVariantId = listingVariant?._id;
    if (!listingVariantId || assignedVariantIds.has(String(masterVariantId))) {
      return;
    }

    const isLiveLayer = mediaLayer === PRODUCT_VIEW_LAYER.LIVE;
    if (isLiveLayer) {
      if (!isMasterVariantLiveOnChannel({
        productListings,
        channelId,
        masterVariantId,
        listingVariant,
      })) {
        return;
      }
    } else if (!isVariantChannelDraft(listingVariant)) {
      return;
    }

    const listingVariantAssets = productsState?.entities?.assets
      ?.listingVariantAssets?.[listingVariantId] || [];
    const layerVariantAssets = filterVariantAssets(listingVariantAssets, {
      linkVariantId: listingVariantId,
      variantType: 'listingVariant',
      layer: mediaLayer,
    });
    if (!layerVariantAssets.length) {
      return;
    }

    const attachedAssetIds = new Set(
      getAttachedAssetsForVariant(product, masterVariantId, productsState)
        .map(({ _id }) => String(_id)),
    );

    const matchedAsset = layerVariantAssets.find(({ _id }) => attachedAssetIds.has(String(_id)));
    if (!matchedAsset) {
      return;
    }

    const assetId = String(matchedAsset._id);
    const normalizedMasterVariantId = String(masterVariantId);
    assignments[assetId] = [...(assignments[assetId] || []), normalizedMasterVariantId];
    assignedVariantIds.add(normalizedMasterVariantId);
  });

  return assignments;
};

export const buildInitialVariantMediaByChannel = ({
  selectedChannels = [],
  product,
  productsState,
  productListings = {},
  masterVariantIds,
  channelActionsByChannelId = {},
  existingVariantMediaByChannel = {},
}) => {
  const variantIds = getSelectedVariantIdsForSync(product, masterVariantIds);

  return selectedChannels.reduce((acc, channelId) => {
    if (Object.prototype.hasOwnProperty.call(existingVariantMediaByChannel, channelId)) {
      acc[channelId] = existingVariantMediaByChannel[channelId];
      return acc;
    }

    const channelAction = channelActionsByChannelId[channelId];

    acc[channelId] = buildChannelVariantMediaAssignments({
      channelId,
      product,
      productsState,
      productListings,
      masterVariantIds: variantIds,
      mediaLayer: getListingMediaLayerForChannelAction(channelAction),
    });
    return acc;
  }, {});
};

export const getAssignedVariantIdsForChannel = (variantMediaByChannel, channelId) => {
  const channelAssignments = variantMediaByChannel?.[channelId] || {};
  const assignedVariantIds = new Set();

  Object.values(channelAssignments).forEach((variantIds) => {
    variantIds.forEach((variantId) => assignedVariantIds.add(variantId));
  });

  return assignedVariantIds;
};

export const countAssignedVariantsForChannel = ({
  variantMediaByChannel,
  channelId,
  totalVariantCount,
}) => {
  const assignedVariantIds = getAssignedVariantIdsForChannel(variantMediaByChannel, channelId);
  return Math.min(assignedVariantIds.size, totalVariantCount);
};

/**
 * Unique assets selected across product-media-by-channel maps.
 */
export const getSelectedAssetsFromMediaByChannel = (assets = [], mediaByChannel = {}) => {
  const selectedIds = new Set();
  Object.values(mediaByChannel).forEach((ids) => {
    (ids || []).forEach((id) => selectedIds.add(String(id)));
  });
  return assets.filter(({ _id }) => selectedIds.has(String(_id)));
};

/**
 * Unique assets assigned in variant media step (keys of per-channel assignments).
 */
export const getAssignedAssetsFromVariantMedia = (variantOptions = [], assignmentsByChannel = {}) => {
  const assignedIds = new Set();
  Object.values(assignmentsByChannel).forEach((channelAssignments) => {
    Object.keys(channelAssignments || {}).forEach((assetId) => {
      assignedIds.add(String(assetId));
    });
  });

  const byId = new Map();
  variantOptions.forEach(({ attachedAssets }) => {
    (attachedAssets || []).forEach((asset) => {
      if (asset?._id) byId.set(String(asset._id), asset);
    });
  });

  return [...assignedIds].map((id) => byId.get(id)).filter(Boolean);
};
